// Sample extracted-document payloads used in mock mode.
// Each entry matches what the real Textract+Normalizer pipeline
// will eventually produce for that document type.

export const DOC_TYPES = Object.freeze({
  invoice: { label: 'Invoice', icon: 'FileText', color: 'sky' },
  receipt: { label: 'Receipt', icon: 'Receipt', color: 'emerald' },
  purchase_order: { label: 'Purchase Order', icon: 'ClipboardList', color: 'violet' },
  utility_bill: { label: 'Utility Bill', icon: 'Zap', color: 'amber' },
  report: { label: 'Financial Report', icon: 'BarChart3', color: 'rose' },
  other: { label: 'Other', icon: 'File', color: 'slate' },
})

const SAMPLE_INVOICE = {
  filename: 'acme-invoice-2026-04.pdf',
  fileType: 'pdf',
  pageCount: 1,
  docType: 'invoice',
  confidence: 96,
  fields: [
    { key: 'invoice_number', label: 'Invoice Number', value: 'ACM-2026-0418', confidence: 99 },
    { key: 'issue_date', label: 'Issue Date', value: '2026-04-18', confidence: 98 },
    { key: 'due_date', label: 'Due Date', value: '2026-05-18', confidence: 97 },
    { key: 'vendor_name', label: 'Vendor', value: 'Acme Industrial Supplies, Inc.', confidence: 95 },
    { key: 'vendor_address', label: 'Vendor Address', value: '142 Foundry Lane, Detroit, MI 48201', confidence: 91 },
    { key: 'bill_to', label: 'Bill To', value: 'Keelara LLC', confidence: 96 },
    { key: 'subtotal', label: 'Subtotal', value: '$2,840.00', confidence: 99 },
    { key: 'tax', label: 'Tax (6%)', value: '$170.40', confidence: 98 },
    { key: 'total', label: 'Total', value: '$3,010.40', confidence: 99 },
  ],
  lineItems: [
    { description: 'Stainless steel brackets (qty 200)', unitPrice: '$8.50', total: '$1,700.00', confidence: 94 },
    { description: 'Industrial fasteners assortment', unitPrice: '$340.00', total: '$340.00', confidence: 92 },
    { description: 'Workshop labor — 10 hrs', unitPrice: '$80.00', total: '$800.00', confidence: 90 },
  ],
  raw: null,  // populated by buildSample()
}

const SAMPLE_RECEIPT = {
  filename: 'starbucks-2026-05-03.jpg',
  fileType: 'jpg',
  pageCount: 1,
  docType: 'receipt',
  confidence: 88,
  fields: [
    { key: 'merchant', label: 'Merchant', value: 'Starbucks #2417', confidence: 96 },
    { key: 'date', label: 'Date', value: '2026-05-03 08:42 AM', confidence: 94 },
    { key: 'subtotal', label: 'Subtotal', value: '$11.85', confidence: 91 },
    { key: 'tax', label: 'Tax', value: '$0.95', confidence: 89 },
    { key: 'total', label: 'Total', value: '$12.80', confidence: 95 },
    { key: 'payment_method', label: 'Payment', value: 'Visa **** 4421', confidence: 87 },
  ],
  lineItems: [
    { description: 'Grande Pike Place', unitPrice: '$3.45', total: '$3.45', confidence: 88 },
    { description: 'Almond Croissant', unitPrice: '$3.95', total: '$3.95', confidence: 86 },
    { description: 'Cold Brew (Venti)', unitPrice: '$4.45', total: '$4.45', confidence: 89 },
  ],
  raw: null,
}

const SAMPLE_PURCHASE_ORDER = {
  filename: 'po-westside-2026-q2.pdf',
  fileType: 'pdf',
  pageCount: 2,
  docType: 'purchase_order',
  confidence: 93,
  fields: [
    { key: 'po_number', label: 'PO Number', value: 'WS-Q2-2026-0142', confidence: 99 },
    { key: 'order_date', label: 'Order Date', value: '2026-05-01', confidence: 97 },
    { key: 'requested_delivery', label: 'Requested Delivery', value: '2026-05-22', confidence: 95 },
    { key: 'vendor_name', label: 'Vendor', value: 'Westside Print Co.', confidence: 96 },
    { key: 'ship_to', label: 'Ship To', value: 'Keelara LLC, 1 Market St, San Francisco, CA 94105', confidence: 92 },
    { key: 'payment_terms', label: 'Payment Terms', value: 'Net 30', confidence: 94 },
    { key: 'total', label: 'Total', value: '$4,275.00', confidence: 98 },
  ],
  lineItems: [
    { description: 'Brochure print run — 5,000 units', unitPrice: '$0.45', total: '$2,250.00', confidence: 95 },
    { description: 'Trade show banners (2-pack)', unitPrice: '$525.00', total: '$1,050.00', confidence: 93 },
    { description: 'Business cards — 10 boxes', unitPrice: '$97.50', total: '$975.00', confidence: 94 },
  ],
  raw: null,
}

const SAMPLE_UTILITY_BILL = {
  filename: 'pgne-bill-2026-04.pdf',
  fileType: 'pdf',
  pageCount: 3,
  docType: 'utility_bill',
  confidence: 78,
  fields: [
    { key: 'account_number', label: 'Account Number', value: '6829-441-022', confidence: 91 },
    { key: 'service_period', label: 'Service Period', value: '2026-03-15 to 2026-04-14', confidence: 89 },
    { key: 'usage_kwh', label: 'Usage (kWh)', value: '847', confidence: 76 },
    { key: 'usage_therms', label: 'Usage (Therms)', value: '14.2', confidence: 64 },
    { key: 'electric_charges', label: 'Electric Charges', value: '$182.34', confidence: 88 },
    { key: 'gas_charges', label: 'Gas Charges', value: '$28.91', confidence: 85 },
    { key: 'total_due', label: 'Total Due', value: '$211.25', confidence: 95 },
    { key: 'due_date', label: 'Due Date', value: '2026-05-15', confidence: 92 },
  ],
  lineItems: [],
  raw: null,
}

const SAMPLES = [SAMPLE_INVOICE, SAMPLE_RECEIPT, SAMPLE_PURCHASE_ORDER, SAMPLE_UTILITY_BILL]

// Bounding box positions for the stylized mock document viewer.
// Coordinates are percentages of the document container so the layout is
// resolution-independent. Real Textract output would replace this with
// page-relative coordinates.
const LAYOUT_TEMPLATES = {
  invoice: {
    title: 'INVOICE',
    leftFields: ['invoice_number', 'issue_date', 'due_date'],
    rightFields: ['vendor_name', 'vendor_address'],
    fullFields: ['bill_to'],
    summaryFields: ['subtotal', 'tax', 'total'],
  },
  receipt: {
    title: 'RECEIPT',
    leftFields: ['merchant', 'date'],
    rightFields: ['payment_method'],
    fullFields: [],
    summaryFields: ['subtotal', 'tax', 'total'],
  },
  purchase_order: {
    title: 'PURCHASE ORDER',
    leftFields: ['po_number', 'order_date', 'requested_delivery'],
    rightFields: ['vendor_name', 'payment_terms'],
    fullFields: ['ship_to'],
    summaryFields: ['total'],
  },
  utility_bill: {
    title: 'UTILITY STATEMENT',
    leftFields: ['account_number', 'service_period', 'due_date'],
    rightFields: ['usage_kwh', 'usage_therms'],
    fullFields: [],
    summaryFields: ['electric_charges', 'gas_charges', 'total_due'],
  },
}

function buildSample(template, { docId, createdAt, status = 'complete' }) {
  const rawJson = {
    document_type: template.docType,
    confidence: template.confidence / 100,
    extracted_at: createdAt,
    fields: template.fields.reduce((acc, f) => ({ ...acc, [f.key]: f.value }), {}),
    line_items: template.lineItems,
    metadata: {
      filename: template.filename,
      page_count: template.pageCount,
      processing_engine: 'papersnap-textract-v1',
    },
  }
  return {
    ...template,
    docId,
    status,
    createdAt,
    raw: rawJson,
    layout: LAYOUT_TEMPLATES[template.docType] || LAYOUT_TEMPLATES.invoice,
  }
}

// Stable demo set — same docs every time the demo user signs in.
export function getDemoDocuments() {
  const now = Date.now()
  const dayMs = 24 * 60 * 60 * 1000
  return [
    buildSample(SAMPLE_INVOICE, { docId: 'demo-doc-001', createdAt: new Date(now - 2 * dayMs).toISOString() }),
    buildSample(SAMPLE_RECEIPT, { docId: 'demo-doc-002', createdAt: new Date(now - 4 * dayMs).toISOString() }),
    buildSample(SAMPLE_PURCHASE_ORDER, { docId: 'demo-doc-003', createdAt: new Date(now - 7 * dayMs).toISOString() }),
    buildSample(SAMPLE_UTILITY_BILL, { docId: 'demo-doc-004', createdAt: new Date(now - 12 * dayMs).toISOString() }),
  ]
}

// Pseudo-random pick for new uploads — driven by filename hash so the
// same file always produces the same fake extraction.
export function pickSampleForFile(file, { docId, createdAt }) {
  const seed = (file?.name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  const template = SAMPLES[seed % SAMPLES.length]
  return buildSample(
    {
      ...template,
      filename: file?.name || template.filename,
      fileType: (file?.name || template.filename).split('.').pop().toLowerCase(),
    },
    { docId, createdAt },
  )
}

export const SAMPLE_TEMPLATES = SAMPLES
